// Logic for the full-page "Upload a book" flow — extracted out of
// UploadFlowPage.tsx so the component only holds presentation. See
// specs/design_handoff_upload-July8 for the flow itself:
//   ONE capture flow, TWO landings by intent (store `uploadEntry`):
//     library → the shipped Book Analysis page (book on the shelf, scored)
//     home    → watch the games build → DoneHome → the set canvas
//   Steps: add → added → details → analyzing (read pages, then analyze text)
//          → found → [home only: building → done].

import { useCallback, useEffect, useRef, useState } from 'react'
import { apiClient } from '@/lib/apiClient'
import { createGuide } from '@/lib/api/guides'
import { fetchBookById } from '@/lib/api/books'
import { mapLearnerStageToBackend } from '@/lib/stageMapping'
import { useGlobalState } from '@/stores/globalState'
import type { Book } from '@/types'
import type { UploadStepId } from '@/modules/upload/UploadChrome'

const MAX_FILES = 35
const ACCEPTED = ['image/jpeg', 'image/png', 'application/pdf', 'image/heic', 'image/heif']
const POLL_MS = 2000

export interface UploadPage {
  id: string
  file: File
  previewUrl: string
}

export function useUploadFlow() {
  const entry = useGlobalState((s) => s.uploadEntry)
  const closeUpload = useGlobalState((s) => s.closeUpload)
  const activeLearner = useGlobalState((s) => s.activeLearner)
  const setActiveModule = useGlobalState((s) => s.setActiveModule)
  const setWorkspaceView = useGlobalState((s) => s.setWorkspaceView)
  const setSelectedBookId = useGlobalState((s) => s.setSelectedBookId)

  const [step, setStep] = useState<UploadStepId>('add')
  const [pages, setPages] = useState<UploadPage[]>([])
  const [title, setTitle] = useState('')
  const [author, setAuthor] = useState('')
  const [isChapterBook, setIsChapterBook] = useState(false)
  const [chapterLabel, setChapterLabel] = useState('')
  const [error, setError] = useState<string | null>(null)

  const [bookId, setBookId] = useState<string | null>(null)
  const [book, setBook] = useState<Book | null>(null)
  const [ocr, setOcr] = useState<{ completed: number; total: number; failed: number }>({ completed: 0, total: 0, failed: 0 })
  const [analysisPhase, setAnalysisPhase] = useState<'reading' | 'analyzing'>('reading')

  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const backLabel = entry === 'home' ? 'Home' : 'Library'

  // Clean up object URLs + polling on unmount.
  useEffect(() => {
    return () => {
      pages.forEach((p) => URL.revokeObjectURL(p.previewUrl))
      if (pollRef.current) clearInterval(pollRef.current)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // ── Add pages ─────────────────────────────────────────────────────────────
  const addFiles = useCallback((incoming: FileList | File[]) => {
    setPages((prev) => {
      const room = MAX_FILES - prev.length
      const accepted = Array.from(incoming)
        .slice(0, room)
        .filter((f) => ACCEPTED.includes(f.type) || /\.(heic|heif)$/i.test(f.name))
      const next = accepted.map((file) => ({ id: crypto.randomUUID(), file, previewUrl: URL.createObjectURL(file) }))
      if (next.length && prev.length === 0 && !title) {
        // Prefill title from the first filename (sans extension).
        setTitle(next[0].file.name.replace(/\.[^.]+$/, ''))
      }
      return [...prev, ...next]
    })
  }, [title])

  const removePage = useCallback((id: string) => {
    setPages((prev) => {
      const p = prev.find((x) => x.id === id)
      if (p) URL.revokeObjectURL(p.previewUrl)
      return prev.filter((x) => x.id !== id)
    })
  }, [])

  // ── Details → create guide, upload pages, trigger OCR, start polling ──────
  const startAnalysis = useCallback(async () => {
    setError(null)
    setStep('analyzing')
    setAnalysisPhase('reading')
    try {
      const { id } = await createGuide({
        title: title.trim() || 'Untitled Book',
        learnerId: activeLearner?.id,
        tenantId: 'default',
        learnerStageAtGen: mapLearnerStageToBackend(activeLearner?.readingStage),
        author: author.trim() || undefined,
        chapterLabel: isChapterBook && chapterLabel.trim() ? chapterLabel.trim() : undefined,
      })
      setOcr({ completed: 0, total: pages.length, failed: 0 })

      await Promise.all(
        pages.map((p, i) => {
          const fd = new FormData()
          fd.append('file', p.file)
          fd.append('pageNumber', String(i + 1))
          return apiClient.post(`/api/content/guides/${id}/images`, fd, { headers: { 'Content-Type': undefined } })
        }),
      )
      await apiClient.post(`/api/content/guides/${id}/process`)

      pollRef.current = setInterval(async () => {
        try {
          const { data } = await apiClient.get(`/api/content/guides/${id}/status`)
          if (data.images) {
            setOcr({ completed: data.images.completed, total: data.images.total, failed: data.images.failed })
          }
          // Phase 1 → 2: pages read, book provisioning underway.
          if (data.status === 'READY') setAnalysisPhase('analyzing')
          if (data.status === 'FAILED') {
            if (pollRef.current) clearInterval(pollRef.current)
            setError('We could not read those pages. Please check the photos and try again.')
            return
          }
          // Ready to summarize once the book + its analysis stats exist.
          if (data.status === 'READY' && data.bookId && data.bookReady) {
            if (pollRef.current) clearInterval(pollRef.current)
            setBookId(data.bookId)
            const b = await fetchBookById(data.bookId)
            setBook(b)
            setStep('found')
          }
        } catch {
          /* transient — keep polling */
        }
      }, POLL_MS)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed. Please try again.')
      setStep('details')
    }
  }, [title, author, isChapterBook, chapterLabel, pages, activeLearner])

  // ── Close (confirm if pages staged) ───────────────────────────────────────
  const handleClose = useCallback(() => {
    if (pages.length > 0 && step !== 'done' && !window.confirm('Leave the upload? Your pages will be discarded.')) return
    if (pollRef.current) clearInterval(pollRef.current)
    closeUpload()
    if (entry === 'home') {
      setActiveModule('MY_GUIDES')
      setWorkspaceView('GALLERY')
    } else {
      setActiveModule('BOOKS')
      setWorkspaceView('GALLERY')
    }
  }, [pages.length, step, closeUpload, entry, setActiveModule, setWorkspaceView])

  // ── Library landing → the shipped Book Analysis page ──────────────────────
  const seeBookAnalysis = useCallback(() => {
    if (!bookId) return
    // Order matters: setActiveModule resets selectedBookId/workspaceView as
    // part of its module-switch cleanup, so it must run FIRST — setting the
    // book id before it wiped the id and Book Analysis rendered "We couldn't
    // load this book".
    setActiveModule('BOOKS')
    setSelectedBookId(bookId)
    setWorkspaceView('BOOK_ANALYSIS')
    closeUpload()
  }, [bookId, setActiveModule, setSelectedBookId, setWorkspaceView, closeUpload])

  return {
    entry,
    step,
    setStep,
    backLabel,
    pages,
    title,
    setTitle,
    author,
    setAuthor,
    isChapterBook,
    setIsChapterBook,
    chapterLabel,
    setChapterLabel,
    error,
    bookId,
    book,
    ocr,
    analysisPhase,
    onAddFiles: addFiles,
    onRemovePage: removePage,
    onAnalyze: startAnalysis,
    onClose: handleClose,
    onSeeAnalysis: seeBookAnalysis,
  }
}
